import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Autocomplete from '@mui/material/Autocomplete';
import { getProjects } from '../api';

interface IntegrationFormProps {
  open: boolean;
  onClose: () => void;
  integration?: any;
  onSubmit: (data: any) => void;
}

const createDefaultConfig = (type: string, integration?: any): Record<string, any> => {
  const existing =
    integration && integration.config && typeof integration.config === 'object'
      ? integration.config
      : {};
  const config: Record<string, any> = { ...existing };
  const ensure = (key: string, value: any) => {
    if (config[key] === undefined) {
      config[key] = value;
    }
  };

  switch (type) {
    case 'bitrix24': {
      ensure('webhook_url', integration?.webhook_url || '');
      ensure('source_id', integration?.source_id || '');
      ensure(
        'assigned_by_id',
        integration?.assigned_by_id !== undefined ? integration.assigned_by_id : '',
      );
      ensure('comment_template', config.comment_template ?? '');
      ensure('use_payload_price', config.use_payload_price ?? true);
      break;
    }
    case 'amocrm': {
      ensure('base_url', config.base_url || '');
      ensure('access_token', config.access_token || '');
      ensure('pipeline_id', config.pipeline_id || '');
      ensure('status_id', config.status_id || '');
      ensure('responsible_user_id', config.responsible_user_id ?? '');
      ensure('source_id', config.source_id ?? '');
      ensure('tag', config.tag ?? 'Call-центр');
      break;
    }
    case '1c': {
      ensure('site', config.site || '');
      ensure('center', config.center || '');
      ensure('central_code', config.central_code || '');
      ensure('type', config.type || '');
      ensure('formtype', config.formtype ?? '');
      ensure('brand', config.brand ?? '');
      ensure('email', config.email ?? '');
      ensure('comment_template', config.comment_template ?? '');
      ensure('adv_template', config.adv_template ?? '');
      ensure('use_test', config.use_test ?? true);
      ensure('url', config.url || 'https://global.agat-group.com/site-order/create/');
      ensure(
        'test_url',
        config.test_url || 'https://global.agat-group.com/site-order/create-test/',
      );
      break;
    }
    case 'email':
    case 'emal': {
      ensure('to', Array.isArray(config.to) ? config.to : config.to || '');
      ensure('subject_prefix', config.subject_prefix || '');
      ensure('subject_template', config.subject_template || 'Новая заявка {{payload.phone}}');
      ensure(
        'body_template',
        config.body_template ||
          [
            'ID лида: {{payload.id}}',
            'Телефон: {{payload.phone}}',
            'Комментарий: {{payload.comment}}',
          ].join('\n'),
      );
      ensure('html_template', config.html_template ?? '');
      break;
    }
    default:
      break;
  }

  return config;
};

export const IntegrationForm: React.FC<IntegrationFormProps> = ({
  open,
  onClose,
  integration,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    project_id: integration?.project_id || '',
    integration_type: integration?.integration_type || 'bitrix24',
    webhook_url: integration?.webhook_url || '',
    source_id: integration?.source_id || '',
    assigned_by_id: integration?.assigned_by_id || '',
  });
  const [configObj, setConfigObj] = useState<Record<string, any>>({});
  const [configText, setConfigText] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const typeOptions = useMemo(
    () => [
      { value: 'bitrix24', label: 'Bitrix24' },
      { value: 'amocrm', label: 'amoCRM' },
      { value: '1c', label: '1C' },
      { value: 'email', label: 'Email' },
      { value: 'emal', label: 'Email (emal)' },
    ],
    [],
  );

  const setConfigState = useCallback(
    (obj: Record<string, any>, options: { preserveText?: boolean } = {}) => {
      setConfigObj(obj);
      if (!options.preserveText) {
        setConfigText(JSON.stringify(obj, null, 2));
      }
    },
    [],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const normalizedType =
      integration && integration.integration_type
        ? integration.integration_type
        : 'bitrix24';
    const initialConfig = createDefaultConfig(normalizedType, integration);
    setFormData({
      project_id: integration?.project_id || '',
      integration_type: normalizedType,
      webhook_url: initialConfig.webhook_url ?? integration?.webhook_url ?? '',
      source_id: initialConfig.source_id ?? integration?.source_id ?? '',
      assigned_by_id:
        initialConfig.assigned_by_id !== undefined && initialConfig.assigned_by_id !== null
          ? String(initialConfig.assigned_by_id)
          : integration?.assigned_by_id
          ? String(integration.assigned_by_id)
          : '',
    });
    setConfigState(initialConfig);
    setConfigError(null);
    setErrors({});
  }, [integration, open, setConfigState]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects();
        setProjects(data);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (formData.integration_type !== 'bitrix24') {
      return;
    }
    const nextWebhook = configObj.webhook_url ?? '';
    const nextSource = configObj.source_id ?? '';
    const nextAssigned =
      configObj.assigned_by_id !== undefined && configObj.assigned_by_id !== null
        ? String(configObj.assigned_by_id)
        : '';
    setFormData(prev => {
      if (
        prev.webhook_url === nextWebhook &&
        prev.source_id === nextSource &&
        prev.assigned_by_id === nextAssigned
      ) {
        return prev;
      }
      return {
        ...prev,
        webhook_url: nextWebhook,
        source_id: nextSource,
        assigned_by_id: nextAssigned,
      };
    });
  }, [configObj, formData.integration_type]);

  const handleIntegrationTypeChange = (nextType: string) => {
    if (nextType === formData.integration_type) {
      return;
    }

    const shouldResetConfig = integration
      ? nextType === integration.integration_type ||
        window.confirm('Изменение типа интеграции сбросит конфигурацию к шаблону. Продолжить?')
      : true;

    setFormData(prev => ({
      ...prev,
      integration_type: nextType,
      webhook_url: '',
      source_id: '',
      assigned_by_id: '',
    }));

    if (shouldResetConfig) {
      const template = createDefaultConfig(nextType, integration);
      setConfigState(template);
      setConfigError(null);
    }
  };

  const handleConfigTextChange = (value: string) => {
    setConfigText(value);
    try {
      const parsed = JSON.parse(value);
      setConfigState(parsed, { preserveText: true });
      setConfigError(null);
    } catch (err: any) {
      setConfigError(err.message || 'Invalid JSON');
    }
  };

  const updateConfigField = useCallback(
    (field: string, value: any) => {
      const updated = { ...configObj };
      if (value === '' || value === undefined || value === null) {
        delete updated[field];
      } else {
        updated[field] = value;
      }
      setConfigState(updated);
      setConfigError(null);
    },
    [configObj, setConfigState],
  );

  const handleLegacyFieldChange =
    (field: 'webhook_url' | 'source_id' | 'assigned_by_id') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));
      let normalized: any = value;
      if (field === 'assigned_by_id' && value !== '') {
        const parsed = Number(value);
        normalized = Number.isNaN(parsed) ? value : parsed;
      }
      updateConfigField(field, normalized);
    };

  const handleSubmit = () => {
    const fieldErrors: Record<string, string> = {};

    if (!formData.project_id) {
      fieldErrors.project_id = 'Project is required';
    }

    let parsedConfig: Record<string, any>;
    try {
      parsedConfig = JSON.parse(configText || '{}');
    } catch (error: any) {
      setErrors(fieldErrors);
      setConfigError(error?.message ? `Invalid JSON: ${error.message}` : 'Invalid JSON');
      return;
    }

    const configValidationErrors: string[] = [];

    switch (formData.integration_type) {
      case 'bitrix24': {
        const webhook = parsedConfig.webhook_url ?? formData.webhook_url;
        const source = parsedConfig.source_id ?? formData.source_id;
        const assignedRaw =
          parsedConfig.assigned_by_id ?? formData.assigned_by_id ?? parsedConfig.responsible_user_id;

        if (!webhook) {
          fieldErrors.webhook_url = 'Webhook URL is required for Bitrix24 integration.';
        }
        if (!source) {
          fieldErrors.source_id = 'Source ID is required for Bitrix24 integration.';
        }
        if (assignedRaw === undefined || assignedRaw === null || assignedRaw === '') {
          fieldErrors.assigned_by_id = 'Assigned by ID is required for Bitrix24 integration.';
        }

        const assignedNumber =
          assignedRaw === undefined || assignedRaw === null || assignedRaw === ''
            ? null
            : Number(assignedRaw);

        if (
          assignedRaw !== undefined &&
          assignedRaw !== null &&
          assignedRaw !== '' &&
          Number.isNaN(assignedNumber)
        ) {
          fieldErrors.assigned_by_id = 'Assigned by ID must be a number for Bitrix24 integration.';
        }

        if (Object.keys(fieldErrors).length > 0) {
          break;
        }

        parsedConfig.webhook_url = webhook;
        parsedConfig.source_id = source;
        parsedConfig.assigned_by_id =
          assignedNumber !== null && !Number.isNaN(assignedNumber) ? assignedNumber : assignedRaw;
        break;
      }
      case 'amocrm': {
        const requiredFields: Array<[string, string]> = [
          ['base_url', 'Base URL is required for amoCRM integration.'],
          ['access_token', 'Access token is required for amoCRM integration.'],
          ['pipeline_id', 'Pipeline ID is required for amoCRM integration.'],
          ['status_id', 'Status ID is required for amoCRM integration.'],
        ];

        requiredFields.forEach(([field, message]) => {
          if (!parsedConfig[field]) {
            configValidationErrors.push(message);
          }
        });

        if (parsedConfig.pipeline_id !== undefined) {
          const pipeline = Number(parsedConfig.pipeline_id);
          if (Number.isNaN(pipeline)) {
            configValidationErrors.push('Pipeline ID must be a number.');
          } else {
            parsedConfig.pipeline_id = pipeline;
          }
        }

        if (parsedConfig.status_id !== undefined) {
          const status = Number(parsedConfig.status_id);
          if (Number.isNaN(status)) {
            configValidationErrors.push('Status ID must be a number.');
          } else {
            parsedConfig.status_id = status;
          }
        }

        if (parsedConfig.responsible_user_id !== undefined && parsedConfig.responsible_user_id !== '') {
          const responsible = Number(parsedConfig.responsible_user_id);
          if (Number.isNaN(responsible)) {
            configValidationErrors.push('Responsible user ID must be a number.');
          } else {
            parsedConfig.responsible_user_id = responsible;
          }
        }

        if (parsedConfig.source_id !== undefined && parsedConfig.source_id !== '') {
          const sourceId = Number(parsedConfig.source_id);
          if (!Number.isNaN(sourceId)) {
            parsedConfig.source_id = sourceId;
          }
        }
        break;
      }
      case '1c': {
        const requiredFields = ['site', 'center', 'central_code', 'type'];
        requiredFields.forEach(field => {
          if (!parsedConfig[field]) {
            configValidationErrors.push(`Поле "${field}" обязательно для интеграции 1C.`);
          }
        });
        break;
      }
      case 'email':
      case 'emal': {
        const recipients = parsedConfig.to;
        if (
          !recipients ||
          (Array.isArray(recipients) && recipients.length === 0) ||
          (typeof recipients === 'string' && recipients.trim() === '')
        ) {
          configValidationErrors.push('Список получателей (поле "to") обязателен для email интеграции.');
        }
        break;
      }
      default:
        break;
    }

    if (Object.keys(fieldErrors).length > 0 || configValidationErrors.length > 0) {
      setErrors(fieldErrors);
      setConfigError(
        configValidationErrors.length > 0 ? configValidationErrors.join('\n') : null,
      );
      return;
    }

    setErrors({});
    setConfigError(null);

    const payload: any = {
      project_id: Number(formData.project_id),
      integration_type: formData.integration_type,
      config: parsedConfig,
    };

    if (formData.integration_type === 'bitrix24') {
      payload.webhook_url = parsedConfig.webhook_url;
      payload.source_id = parsedConfig.source_id;
      if (
        parsedConfig.assigned_by_id !== undefined &&
        parsedConfig.assigned_by_id !== null &&
        parsedConfig.assigned_by_id !== ''
      ) {
        payload.assigned_by_id = Number(parsedConfig.assigned_by_id);
      }
    }

    setConfigState(parsedConfig);
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {integration ? t('edit') || 'Edit' : t('create') || 'Create'} Integration
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal" error={!!errors.project_id}>
          <Autocomplete
            options={projects}
            getOptionLabel={option => option.name}
            value={projects.find(p => String(p.id) === String(formData.project_id)) || null}
            onChange={(event, newValue) =>
              setFormData({ ...formData, project_id: newValue ? newValue.id : '' })
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => (
              <TextField
                {...params}
                label="Project"
                required
                margin="normal"
                placeholder="Найти проект..."
                error={!!errors.project_id}
                helperText={errors.project_id}
              />
            )}
          />
        </FormControl>

        <FormControl fullWidth margin="normal" error={!!errors.integration_type}>
          <InputLabel>Integration Type</InputLabel>
          <Select
            value={formData.integration_type}
            onChange={e => handleIntegrationTypeChange(e.target.value as string)}
            label="Integration Type"
          >
            {typeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {errors.integration_type && <FormHelperText>{errors.integration_type}</FormHelperText>}
        </FormControl>

        {formData.integration_type === 'bitrix24' && (
          <>
            <TextField
              fullWidth
              label="Webhook URL"
              value={formData.webhook_url}
              onChange={handleLegacyFieldChange('webhook_url')}
              margin="normal"
              required
              error={!!errors.webhook_url}
              helperText={errors.webhook_url}
            />
          </>
        )}

        {(formData.integration_type === 'email' || formData.integration_type === 'emal') && (
          <TextField
            fullWidth
            label="Тег (префикс темы письма)"
            value={configObj.subject_prefix || ''}
            onChange={e => updateConfigField('subject_prefix', e.target.value)}
            margin="normal"
            placeholder="Дентал Холл"
            helperText="Добавляется в начало темы письма в квадратных скобках, например: [Дентал Холл] Новая заявка +7..."
          />
        )}

        <TextField
          fullWidth
          label="Config (JSON)"
          value={configText}
          onChange={e => handleConfigTextChange(e.target.value)}
          margin="normal"
          multiline
          minRows={8}
          maxRows={20}
          error={!!configError}
          helperText={configError || 'Настройки интеграции в формате JSON'}
          inputProps={{
            spellCheck: false,
            style: { fontFamily: 'monospace' },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};


